import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import {Plus, Edit2, Trash2, Shield, Eye, EyeOff} from 'lucide-react'

interface AdminUser {
  _id: string
  userId: string
  email: string
  userName: string
  role: "super_admin" | "store_admin" | "finance" | "marketing" | "roaster" | "sales"
  status: "active" | "inactive" | "suspended"
  permissions: {
    canManageUsers: boolean
    canManageProducts: boolean
    canManageOrders: boolean
    canViewFinance: boolean
    canManageMarketing: boolean
    canManageStores: boolean
  }
  storeAccess?: string[]
  lastLoginAt?: string
  lastLoginIp?: string
  twoFactorEnabled: boolean
  createdAt: string
  updatedAt: string
}

const roleLabels: Record<string, string> = {
  super_admin: "超級管理員",
  store_admin: "店長",
  finance: "財務",
  marketing: "行銷",
  roaster: "烘豆師",
  sales: "業務"
}

const AdminUserManager: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>("")
  const [showForm, setShowForm] = useState(false)
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [formData, setFormData] = useState({
    userId: "",
    email: "",
    userName: "",
    role: "marketing" as AdminUser["role"],
    status: "active" as AdminUser["status"],
    twoFactorEnabled: false,
    permissions: {
      canManageUsers: false,
      canManageProducts: false,
      canManageOrders: false,
      canViewFinance: false,
      canManageMarketing: false,
      canManageStores: false
    }
  })

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    setLoading(true)
    setError("")
    try {
      const { list } = await lumi.entities.admin_users.list({
        sort: { createdAt: -1 }
      })
      setUsers(list || [])
    } catch (error) {
      console.error("Failed to fetch users:", error)
      setError("無法載入使用者資料，請檢查資料庫連線或重新整理頁面")
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const now = new Date().toISOString()
      if (editingUser) {
        await lumi.entities.admin_users.update(editingUser._id, {
          ...formData,
          updatedAt: now
        })
      } else {
        await lumi.entities.admin_users.create({
          ...formData,
          lastLoginAt: "",
          lastLoginIp: "",
          storeAccess: [],
          createdAt: now,
          updatedAt: now
        })
      }
      await fetchUsers()
      resetForm()
    } catch (error) {
      console.error("Failed to save user:", error)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("確定要刪除此使用者嗎？")) return
    try {
      await lumi.entities.admin_users.delete(id)
      await fetchUsers()
    } catch (error) {
      console.error("Failed to delete user:", error)
    }
  }

  const handleEdit = (user: AdminUser) => {
    setEditingUser(user)
    setFormData({
      userId: user.userId,
      email: user.email,
      userName: user.userName,
      role: user.role,
      status: user.status,
      twoFactorEnabled: user.twoFactorEnabled,
      permissions: user.permissions
    })
    setShowForm(true)
  }

  const resetForm = () => {
    setShowForm(false)
    setEditingUser(null)
    setFormData({
      userId: "",
      email: "",
      userName: "",
      role: "marketing",
      status: "active",
      twoFactorEnabled: false,
      permissions: {
        canManageUsers: false,
        canManageProducts: false,
        canManageOrders: false,
        canViewFinance: false,
        canManageMarketing: false,
        canManageStores: false
      }
    })
  }

  const updatePermission = (key: keyof AdminUser["permissions"]) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [key]: !formData.permissions[key]
      }
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">載入中...</div>
      </div>
    )
  }

  return (
    <div className="p-8">
      {error && (
        <div className="mb-6 p-4 bg-red-900 border border-red-700 text-red-200">
          <p className="font-medium">錯誤</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-light tracking-wider mb-2">使用者管理</h2>
          <p className="text-gray-400">管理內部員工帳號與權限</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          新增使用者
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-950 border border-gray-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-800">
              <h3 className="text-xl font-light tracking-wider">
                {editingUser ? "編輯使用者" : "新增使用者"}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">使用者 ID</label>
                  <input
                    type="text"
                    value={formData.userId}
                    onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                    className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                    required
                    disabled={!!editingUser}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">姓名</label>
                  <input
                    type="text"
                    value={formData.userName}
                    onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                    className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">角色</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as AdminUser["role"] })}
                    className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                  >
                    {Object.entries(roleLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">狀態</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as AdminUser["status"] })}
                    className="w-full px-4 py-2 bg-black border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                  >
                    <option value="active">啟用</option>
                    <option value="inactive">停用</option>
                    <option value="suspended">暫停</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-3">權限設定</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: "canManageUsers", label: "管理使用者" },
                    { key: "canManageProducts", label: "管理商品" },
                    { key: "canManageOrders", label: "管理訂單" },
                    { key: "canViewFinance", label: "查看財務" },
                    { key: "canManageMarketing", label: "管理行銷" },
                    { key: "canManageStores", label: "管理分店" }
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.permissions[key as keyof AdminUser["permissions"]]}
                        onChange={() => updatePermission(key as keyof AdminUser["permissions"])}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-300">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.twoFactorEnabled}
                  onChange={(e) => setFormData({ ...formData, twoFactorEnabled: e.target.checked })}
                  className="w-4 h-4"
                />
                <label className="text-sm text-gray-300">啟用雙因素認證</label>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-800">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  {editingUser ? "更新" : "建立"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-6 py-3 bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  取消
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-gray-950 border border-gray-800">
        <table className="w-full">
          <thead className="border-b border-gray-800">
            <tr className="text-left">
              <th className="px-6 py-4 text-sm font-light text-gray-400">姓名</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">Email</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">角色</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">狀態</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">最後登入</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">操作</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id} className="border-b border-gray-800 hover:bg-gray-900 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-100">{user.userName}</span>
                    {user.twoFactorEnabled && (
                      <Shield size={14} className="text-green-500" title="已啟用雙因素認證" />
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-300">{user.email}</td>
                <td className="px-6 py-4">
                  <span className="px-3 py-1 bg-blue-600 text-white text-xs">
                    {roleLabels[user.role]}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 text-xs ${
                    user.status === "active" ? "bg-green-600 text-white" :
                    user.status === "inactive" ? "bg-gray-600 text-white" :
                    "bg-red-600 text-white"
                  }`}>
                    {user.status === "active" ? "啟用" : user.status === "inactive" ? "停用" : "暫停"}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-400 text-sm">
                  {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString("zh-TW") : "尚未登入"}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(user)}
                      className="p-2 hover:bg-gray-800 transition-colors"
                      title="編輯"
                    >
                      <Edit2 size={16} className="text-gray-400" />
                    </button>
                    <button
                      onClick={() => handleDelete(user._id)}
                      className="p-2 hover:bg-gray-800 transition-colors"
                      title="刪除"
                    >
                      <Trash2 size={16} className="text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            尚無使用者資料
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminUserManager
